/**
 * SRES · GIS Studio page (Sprint P37y · Priority 2 · Feb 2026)
 *
 * A single page that ties together:
 *   1. Boundary upload + preview + approve + version history (Priority 2A)
 *   2. Interactive Map Viewer with layer catalogue (Priority 2B)
 *   3. Collar-vs-Boundary 7-status validation (Priority 3)
 *   4. Export controls:
 *      • Complete GIS Map Package PDF
 *      • Individual / Selected map PDFs
 *      • GIS Map ZIP
 *      • Complete Continuous Drillhole Log Book PDF
 *      • Individual / Selected drillhole PDFs
 *      • Drillhole Logs ZIP
 *      • Persistent generation queue (waiting/generating/ready/failed)
 *
 * Downloads work independently — no CPR generation required.
 */
import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  ArrowLeft, MapPin, UploadSimple, CheckCircle,
  ClockCounterClockwise, CircleNotch, Warning, XCircle, DownloadSimple,
  MapTrifold, GlobeHemisphereWest, Compass, Sparkle, Files,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import BoundaryPanel from "./BoundaryPanel";
import CollarValidator from "./CollarValidator";
import ExportQueue from "./ExportQueue";
import MapGallery from "./MapGallery";
import CRSCorrectionPreviewModal from "./CRSCorrectionPreviewModal";
import CRSAuditTrailPanel from "./CRSAuditTrailPanel";
import { useCollarValidationQuery, collarIssueCount } from "./useCollarValidation";

const TABS = [
  { id: "boundary",  label: "Boundary",  Icon: MapTrifold },
  { id: "viewer",    label: "Map Gallery", Icon: GlobeHemisphereWest },
  { id: "validate",  label: "Validation", Icon: Compass },
  { id: "exports",   label: "Exports",   Icon: DownloadSimple },
];

export default function GISStudio() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("boundary");
  const [crsModalOpen, setCrsModalOpen] = useState(false);

  const boundaryQ = useQuery({
    queryKey: ["gis-boundary", pid],
    queryFn: () => api.get(`/projects/${pid}/gis/boundary`).then((r) => r.data),
    refetchInterval: 30_000,
  });

  // Iter81 · Feb 2026 · shared collar-validation query. Fetched once
  // (cache-shared with CollarValidator) so the "X issues" pill badge
  // stays live even while the panel is unmounted.
  const validationQ = useCollarValidationQuery(pid, 25);
  const issueCount = collarIssueCount(validationQ.data);

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50" data-testid="gis-studio">
      <header className="shrink-0 bg-white border-b border-slate-200 z-10">
        <div className="px-6 py-3 flex items-center gap-4">
          <button onClick={() => nav(`/app/projects/${pid}/setup`)}
                     className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-500 hover:text-brand-blue"
                     data-testid="btn-back-to-setup">
            <ArrowLeft size={12} /> Setup
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.28em] text-slate-500">SRES · GIS Studio</div>
            <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900 leading-none">
              Boundary · Maps · Exports
            </h1>
          </div>
          <button
            type="button"
            onClick={() => setCrsModalOpen(true)}
            className="shrink-0 inline-flex items-center gap-1.5 rounded border border-brand-blue bg-white px-3 py-1.5 text-[11.5px] font-semibold text-brand-blue hover:bg-brand-blue hover:text-white transition-colors"
            data-testid="btn-crs-correction-preview"
            title="Upload a KML/KMZ/CSV and preview the CRS correction before saving">
            KML CRS Preview
          </button>
        </div>
        <nav className="px-6 flex gap-1 border-t border-slate-100 bg-slate-50/50" data-testid="gis-tabs">
          {TABS.map((t) => {
            const showBadge = t.id === "validate" && issueCount > 0;
            return (
              <button key={t.id}
                         onClick={() => setTab(t.id)}
                         data-testid={`tab-${t.id}`}
                         className={`inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold uppercase tracking-widest border-b-2 transition
                            ${tab === t.id
                              ? "border-brand-blue text-brand-blue"
                              : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                <t.Icon size={12} weight="duotone" /> {t.label}
                {showBadge && (
                  <span data-testid="tab-validate-badge"
                            title={`${issueCount} collar issue${issueCount === 1 ? "" : "s"} found`}
                            className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold tabular-nums shadow-sm ring-1 ring-amber-600/40 animate-pulse">
                    {issueCount > 99 ? "99+" : issueCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-4"
                data-testid="gis-studio-main">
        <div className="max-w-7xl mx-auto pb-8">
          {tab === "boundary" && <BoundaryPanel pid={pid} boundary={boundaryQ.data} refetch={boundaryQ.refetch} />}
          {tab === "viewer"    && <MapGallery pid={pid} boundary={boundaryQ.data} />}
          {tab === "validate"  && <CollarValidator pid={pid} />}
          {tab === "exports"   && <ExportQueue pid={pid} />}
        </div>
      </main>
      <div className="max-w-6xl mx-auto px-6 pb-8 -mt-2">
        <CRSAuditTrailPanel pid={pid} />
      </div>
      <CRSCorrectionPreviewModal
        pid={pid} open={crsModalOpen}
        onClose={() => setCrsModalOpen(false)}
        onApproved={() => boundaryQ.refetch()}
      />
    </div>
  );
}
